/**
 * @fileoverview A plant's name as the backend says it: the one place the app
 * reads it from, so no screen composes a name of its own (the owner's rule,
 * 2026-10-14: the backend is for business logic, the front end is display).
 *
 * Every plant GET /plants sends carries `names` (services/grow/plantNaming):
 * its full name ("Devil's Ear Lettuce", "Butterhead Lettuce", "Sage"), its
 * title as pieces with what tells it from another of the same name in bold,
 * and the crop, kind and variety words the share frame and the About sheet
 * print. These helpers read them.
 *
 * Where: the Plants page (PlantCard, PlantRow, PlantTile, PlantQuickLook,
 * plantViews), the archive, the bed cards, the photo dialogs, Today's planting
 * jobs, the Weather page, the share sheet and the photo tray.
 * API: none.
 *
 * @module plantName
 */

import type { NamePart, Plant } from '@/shared/types/plant';

/** The least a plant needs to be named: its names. */
type Named = Pick<Plant, 'names'>;

/**
 * A plant's full name: "Devil's Ear Lettuce".
 *
 * @param plant - The plant, or nothing.
 * @returns The name; '' for nothing.
 */
export function plantName(plant: Named | null | undefined): string {
  if (!plant) return '';
  return plant.names.full;
}

/**
 * A plant as a gardener tells it apart in a list of the garden: its one name and the bed it is in ("Sweet Horizon peas · Front bed"),
 * since two beds often grow the same thing.
 *
 * @param plant - The plant.
 * @returns The name, then " · " and the bed when it has one.
 */
export function plantWithBed(plant: Pick<Plant, 'names' | 'location'>): string {
  return [plantName(plant), plant.location?.name].filter(Boolean).join(' · ');
}

/**
 * A plant's title as pieces: its full name, then what tells it from another
 * of the same name in the garden (its bed, then the day it was added), set in
 * bold by the screen (NameParts).
 *
 * A screen that shows the bed on a line of its own (the Plants page's card,
 * row and tile) asks for the title without it, so the bed is not said twice
 * ("Sweet Basil · Seed Starting Area, Top Shelf" over "Seed Starting Area, Top
 * Shelf"; owner, 2026-10-14). The bed line tells such twins apart there.
 *
 * @param plant - The plant.
 * @param options - bedShown: the screen shows the plant's bed itself.
 * @returns The pieces, never empty.
 */
export function plantTitle(plant: Named | null | undefined, options: { bedShown?: boolean } = {}): NamePart[] {
  const parts = plant?.names.title;
  if (!parts || parts.length === 0) return [{ text: plantName(plant) }];
  return options.bedShown ? parts.filter((part) => part.kind !== 'bed') : parts;
}

/**
 * A plant's title as plain text, for a label or a sort: "Sweet Basil · Side garden · added Oct 11".
 *
 * @param plant - The plant.
 * @returns The words; '' for nothing.
 */
export function plantShown(plant: Named | null | undefined): string {
  return plant?.names.shown ?? plantTitle(plant).map((part) => part.text).join('');
}

/**
 * The two words the share frame prints on its Name and Variety switches: the
 * crop as a name ("Lettuce") and what is the plant's own beside it ("Devil's
 * Ear"; "Butterhead" for a "Not sure" plant; '' for one that is only its
 * crop).
 *
 * @param plant - The plant.
 * @returns The crop's name and the plant's own words.
 */
export function shareWords(plant: Named | null | undefined): { name: string; variety: string } {
  if (!plant) return { name: '', variety: '' };
  return { name: plant.names.cropName, variety: plant.names.distinct };
}
